import React, { useMemo } from 'react';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { isCaseSettled, isOfferSent, isAssignmentComplete } from '../SummaryCaseTable/utils/helpers.js';

/**
 * Unified dialog for canceling both settlements and offers
 * Clearly communicates what action will be taken for each type of case
 */
const UnifiedCancelDialog = ({
  open,
  onClose,
  rowsToCancel,
  cancellingOffers,
  onConfirm,
  getNegotiationObj
}) => {
  // Categorize cases by their status
  const categorizedCases = useMemo(() => {
    if (!rowsToCancel || !getNegotiationObj) {
      return { settled: [], finalized: [], other: [] };
    }

    const settled = [];
    const finalized = [];
    const other = [];

    rowsToCancel.forEach(row => {
      const negotiationCase = getNegotiationObj.cases.find(c => c.parcel_id === row.parcel_id);
      
      if (!negotiationCase) {
        other.push(row);
        return;
      }

      // Check if case is settled
      if (isCaseSettled(negotiationCase)) {
        settled.push({
          ...row,
          negotiationCase,
          action: 'Cancel Settlement',
          description: 'Remove settlement status and allow further negotiations'
        });
      } else if (
        isAssignmentComplete(negotiationCase, 'CaseReview')
        || isOfferSent(negotiationCase.Negotiations, negotiationCase.parcel_id)
      ) {
        finalized.push({
          ...row,
          negotiationCase,
          action: 'Cancel Offer',
          description: 'Cancel the offer'
        });
      } else {
        other.push({
          ...row,
          negotiationCase,
          action: 'No Action',
          description: 'Case is not reviewed and has no live offer'
        });
      }
    });

    return { settled, finalized, other };
  }, [rowsToCancel, getNegotiationObj]);

  const { settled, finalized, other } = categorizedCases;
  const actionableCases = settled.length + finalized.length;

  const getDialogTitle = () => {
    if (settled.length > 0 && finalized.length > 0) {
      return 'Cancel Settlements & Offers';
    } else if (settled.length > 0) {
      return 'Cancel Settlements';
    } else if (finalized.length > 0) {
      return 'Cancel Offers';
    } else {
      return 'Cancel Actions';
    }
  };

  const getDialogDescription = () => {
    if (actionableCases === 0) {
      return 'No cases can be cancelled. Selected cases are not settled, reviewed, or live offers.';
    } else if (settled.length > 0 && finalized.length > 0) {
      return `You are about to cancel ${settled.length} settlement(s) and ${finalized.length} offer(s). This will remove their current status and allow further negotiations.`;
    } else if (settled.length > 0) {
      return `You are about to cancel ${settled.length} settlement(s). This will remove the settlement status and allow further negotiations.`;
    } else if (finalized.length > 0) {
      return `You are about to cancel ${finalized.length} offer(s). This will cancel the offer.`;
    }
    return '';
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const renderCaseList = (cases, title, color = 'primary') => {
    if (cases.length === 0) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: `${color}.main` }}>
          {title} ({cases.length})
        </Typography>
        <Box sx={{ maxHeight: 150, overflow: 'auto', border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1 }}>
          {cases.map((caseItem, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {caseItem.Address || caseItem.parcel_id}
              </Typography>
              <Chip 
                label={caseItem.action} 
                size="small" 
                color={color}
                variant="outlined"
                sx={{ ml: 1, fontSize: '0.7rem' }}
              />
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {cases[0]?.description}
        </Typography>
      </Box>
    );
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={onClose}
      aria-labelledby="unified-cancel-dialog-title"
      aria-describedby="unified-cancel-dialog-description"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="unified-cancel-dialog-title">
        {getDialogTitle()}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="unified-cancel-dialog-description" sx={{ mb: 2 }}>
          {getDialogDescription()}
        </DialogContentText>

        {/* Show categorized cases */}
        {actionableCases > 0 && (
          <Box sx={{ mb: 2 }}>
            {renderCaseList(settled, 'Settlements to Cancel', 'error')}
            {settled.length > 0 && finalized.length > 0 && <Divider sx={{ my: 1 }} />}
            {renderCaseList(finalized, 'Offers to Cancel', 'warning')}
          </Box>
        )}

        {/* Show cases that cannot be cancelled */}
        {other.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {renderCaseList(other, 'Cases Not Affected', 'default')}
          </Box>
        )}

        {/* Warning message */}
        {actionableCases > 0 && (
          <DialogContentText sx={{ mt: 2, fontStyle: 'italic', color: 'error.main', fontWeight: 'bold' }}>
            This action cannot be undone.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          color="primary"
          disabled={cancellingOffers}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={cancellingOffers || actionableCases === 0}
          startIcon={cancellingOffers ? <CircularProgress size={16} /> : null}
        >
          {cancellingOffers ? 'Processing...' : `Confirm (${actionableCases} cases)`}
        </Button>
      </DialogActions>
    </StableMuiDialog>
  );
};

export default UnifiedCancelDialog;
